// src/pages/admin/AdminReferralsPage.tsx
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  fetchReferralConfig,
  fetchReferrals,
  fetchReferralGroups,
  completeReferral,
  type ReferralConfig,
  type ReferralRow,
  type ReferralGroup,
} from '../../api/adminReferrals';
import classNames from 'classnames';

// 設定の取得元バッジ（code=予約コード一致で自動採用 / env=旧IDフォールバック / unset=該当なし）
const SourceBadge: React.FC<{ src?: 'code' | 'env' | 'unset' }> = ({ src }) => {
  if (src === 'code') return <span className="text-xs px-1.5 py-0.5 rounded bg-green-100 text-green-700">コード自動一致</span>;
  if (src === 'env') return <span className="text-xs px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">.env(旧方式)</span>;
  return <span className="text-xs px-1.5 py-0.5 rounded bg-red-100 text-red-700">未設定</span>;
};

// 日本時間(UTC+9, Asia/Tokyo)で YYYY-MM-DD HH:mm に整形
const fmt = (s?: string | null) => {
  if (!s) return '—';
  try {
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) {
      // 解析できない場合は元の文字列を返す
      return s;
    }

    const opts: Intl.DateTimeFormatOptions = {
      timeZone: 'Asia/Tokyo',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    };

    const parts = new Intl.DateTimeFormat('ja-JP', opts).formatToParts(d);
    const get = (type: string) => parts.find(p => p.type === type)?.value || '';

    const y = get('year');
    const m = get('month');
    const day = get('day');
    const h = get('hour');
    const min = get('minute');

    return `${y}-${m}-${day} ${h}:${min}`;
  } catch {
    return s;
  }
};

const StatusPill: React.FC<{ s: ReferralRow['status'] }> = ({ s }) => {
  const { t } = useTranslation();
  const map: Record<string, string> = {
    pending: 'bg-amber-100 text-amber-700',
    completed: 'bg-green-100 text-green-700',
    canceled: 'bg-gray-200 text-gray-700',
  };
  const label: Record<string, string> = {
    pending: t('admin.referrals.status.pending') || 'pending',
    completed: t('admin.referrals.status.completed') || 'completed',
    canceled: t('admin.referrals.status.canceled') || 'canceled',
  };
  return <span className={classNames('px-2 py-0.5 rounded text-xs', map[s])}>{label[s]}</span>;
};

const AdminReferralsPage: React.FC = () => {
  const { t } = useTranslation();

  useEffect(() => {
    const token = localStorage.getItem('token');
    const role = localStorage.getItem('role');
    if (!token || role !== 'admin') {
      window.location.href = '/login/admin';
    }
  }, []);

  const [cfg, setCfg] = useState<ReferralConfig | null>(null);
  const [cfgErr, setCfgErr] = useState<string | null>(null);

  const [rows, setRows] = useState<ReferralRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // 表示モード: 一覧 / グループ（紹介者→被紹介者）
  const [viewMode, setViewMode] = useState<'list' | 'group'>('list');
  const [groups, setGroups] = useState<ReferralGroup[]>([]);
  const [groupLoading, setGroupLoading] = useState(false);

  const MEMBER_TYPE_LABEL: Record<string, string> = { employee: '社員', family: '家族', friend: '友人' };
  const MEMBER_TYPE_STYLE: Record<string, string> = {
    employee: 'bg-indigo-100 text-indigo-700',
    family: 'bg-emerald-100 text-emerald-700',
    friend: 'bg-amber-100 text-amber-700',
  };

  const loadGroups = async () => {
    setGroupLoading(true);
    try {
      const data = await fetchReferralGroups(q.trim() || undefined);
      setGroups(data);
    } catch (e: any) {
      setErr(e?.response?.data?.detail || e?.message || '取得に失敗しました');
    } finally {
      setGroupLoading(false);
    }
  };

  const [q, setQ] = useState('');
  const [status, setStatus] = useState<'pending' | 'completed' | 'canceled' | ''>('');
  const [orderBy, setOrderBy] = useState<'id' | 'created_at' | 'completed_at' | 'status'>('created_at');
  const [orderDir, setOrderDir] = useState<'asc' | 'desc'>('desc');
  const [limit, setLimit] = useState(50);
  const [offset, setOffset] = useState(0);

  const [dlgOpen, setDlgOpen] = useState(false);
  const [dlgTarget, setDlgTarget] = useState<ReferralRow | null>(null);
  const [dlgReferredUserId, setDlgReferredUserId] = useState<number | ''>('');
  const [dlgReferrerCouponId, setDlgReferrerCouponId] = useState<number | ''>('');
  const [dlgReferredCouponId, setDlgReferredCouponId] = useState<number | ''>('');
  const [dlgBusy, setDlgBusy] = useState(false);
  const [dlgMsg, setDlgMsg] = useState<string | null>(null);

  const loadCfg = async () => {
    try {
      setCfgErr(null);
      const c = await fetchReferralConfig();
      setCfg(c);
      setDlgReferrerCouponId(c.referrer_coupon_id || '');
      setDlgReferredCouponId(c.referred_coupon_id || '');
    } catch (e: any) {
      setCfgErr(e?.message || t('admin.referrals.config.error') || '設定の取得に失敗しました');
    }
  };

  const loadRows = async (reset = false) => {
    setLoading(true);
    setErr(null);
    try {
      const data = await fetchReferrals({
        q: q.trim() || undefined,
        status: status || undefined,
        order_by: orderBy,
        order_dir: orderDir,
        limit,
        offset: reset ? 0 : offset,
      });
      setRows(data);
      if (reset) setOffset(0);
    } catch (e: any) {
      setErr(e?.response?.data?.detail || e?.message || (t('common.error') as string) || '取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCfg();
    loadRows(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const nextPage = () => setOffset((o) => o + limit);
  const prevPage = () => setOffset((o) => Math.max(0, o - limit));

  useEffect(() => {
    loadRows(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offset, limit, orderBy, orderDir]);

  const openComplete = (r: ReferralRow) => {
    setDlgTarget(r);
    setDlgReferredUserId(r.referred_user_id || '');
    setDlgMsg(null);
    setDlgOpen(true);
  };

  const submitComplete = async () => {
    if (!dlgTarget?.id) return;
    if (!dlgReferredUserId || !dlgReferrerCouponId || !dlgReferredCouponId) {
      setDlgMsg(t('admin.referrals.dialog.msg.missing') || '未入力の項目があります');
      return;
    }
    setDlgBusy(true);
    setDlgMsg(null);
    try {
      const res = await completeReferral(dlgTarget.id, {
        referred_user_id: Number(dlgReferredUserId),
        referrer_coupon_id: Number(dlgReferrerCouponId),
        referred_coupon_id: Number(dlgReferredCouponId),
      });
      if (res.ok) {
        setDlgOpen(false);
        await loadRows(false);
      } else {
        setDlgMsg(t('admin.referrals.dialog.msg.fail') || '完了処理に失敗しました');
      }
    } catch (e: any) {
      setDlgMsg(e?.response?.data?.detail || e?.message || (t('admin.referrals.dialog.msg.fail') as string));
    } finally {
      setDlgBusy(false);
    }
  };

  // 並び替えフィールドの i18n（admin.referrals.search.* を使用）
  const orderFieldLabel = (k: 'id' | 'created_at' | 'completed_at' | 'status') => {
    if (k === 'created_at') return (t('admin.referrals.search.created_at') as string) || '作成日時';
    if (k === 'completed_at') return (t('admin.referrals.search.completed_at') as string) || '完了日時';
    if (k === 'id') return (t('admin.referrals.search.id') as string) || 'ID';
    if (k === 'status') return (t('admin.referrals.search.status') as string) || 'ステータス';
    return k;
  };

  return (
    <div className="w-full p-4">
      <h2 className="text-2xl font-bold mb-4">{t('admin.referrals.title') || '招待特典の管理'}</h2>

      {/* 表示モード切替 */}
      <div className="inline-flex rounded-lg border border-gray-200 overflow-hidden mb-4">
        <button
          onClick={() => setViewMode('list')}
          className={classNames('px-4 py-1.5 text-sm', viewMode === 'list' ? 'bg-coral text-white' : 'bg-white text-gray-600')}
        >
          一覧
        </button>
        <button
          onClick={() => { setViewMode('group'); loadGroups(); }}
          className={classNames('px-4 py-1.5 text-sm', viewMode === 'group' ? 'bg-coral text-white' : 'bg-white text-gray-600')}
        >
          グループ表示
        </button>
      </div>

      {/* 設定カード */}
      <div className="bg-white rounded-xl shadow p-4 mb-6">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold">{t('admin.referrals.config.title') || '現在の招待用クーポン設定'}</h3>
          <button
            onClick={loadCfg}
            className="px-3 py-1.5 rounded border hover:bg-gray-50 text-sm"
          >
            {t('common.reload') || '再読込'}
          </button>
        </div>
        {cfgErr ? (
          <p className="text-sm text-red-600 mt-2">{cfgErr}</p>
        ) : !cfg ? (
          <p className="text-sm text-gray-500 mt-2">{t('common.loading') || '読み込み中…'}</p>
        ) : (
          <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
            {/* 紹介者に付与するクーポン（予約コードで自動照合） */}
            <div className="border rounded-lg p-3">
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm text-gray-500">{t('admin.referrals.config.referrer') || '紹介者に付与するクーポン'}</span>
                <SourceBadge src={cfg.referrer_source} />
              </div>
              <p className="text-sm">
                予約コード: <span className="font-mono bg-gray-100 px-1.5 py-0.5 rounded">{cfg.referrer_code || '—'}</span>
              </p>
              {cfg.referrer_coupon ? (
                <div className="text-sm text-gray-700 mt-1">
                  <div>採用中: <span className="font-semibold">{cfg.referrer_coupon.name || '—'}</span>（ID {cfg.referrer_coupon.id}）</div>
                  <div className="text-xs text-gray-500">
                    コード: <span className="font-mono">{cfg.referrer_coupon.code || '—'}</span>{' ・ '}{cfg.referrer_coupon.is_active ? '有効' : '無効'}
                  </div>
                </div>
              ) : (
                <p className="text-sm text-red-500 mt-1">上記コードを持つ有効なクーポンがありません。該当券のコードを設定してください。</p>
              )}
            </div>

            {/* 被紹介者に付与するクーポン（予約コードで自動照合） */}
            <div className="border rounded-lg p-3">
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm text-gray-500">{t('admin.referrals.config.referred') || '被紹介者に付与するクーポン'}</span>
                <SourceBadge src={cfg.referred_source} />
              </div>
              <p className="text-sm">
                予約コード: <span className="font-mono bg-gray-100 px-1.5 py-0.5 rounded">{cfg.referred_code || '—'}</span>
              </p>
              {cfg.referred_coupon ? (
                <div className="text-sm text-gray-700 mt-1">
                  <div>採用中: <span className="font-semibold">{cfg.referred_coupon.name || '—'}</span>（ID {cfg.referred_coupon.id}）</div>
                  <div className="text-xs text-gray-500">
                    コード: <span className="font-mono">{cfg.referred_coupon.code || '—'}</span>{' ・ '}{cfg.referred_coupon.is_active ? '有効' : '無効'}
                  </div>
                </div>
              ) : (
                <p className="text-sm text-red-500 mt-1">上記コードを持つ有効なクーポンがありません。該当券のコードを設定してください。</p>
              )}
            </div>
          </div>

          <p className="text-xs text-gray-500 mt-3">
            紹介特典は<b>予約コードで自動照合</b>されます。上記の予約コードを持つ有効なクーポンが自動的に使われます（この画面での選択・保存は不要）。
            設定するには「クーポン」ページで該当2枚のコードを上記の予約コードに変更してください。コードは全環境で共通で、数字IDに依存しません。
            <br />
            <span className="text-amber-600">※「.env(旧方式)」表示中は、予約コード一致が無いため旧IDにフォールバックしています。移行するにはコードを設定してください。</span>
          </p>
          </>
        )}
      </div>

      {/* グループ表示（紹介者→被紹介者） */}
      {viewMode === 'group' && (
        <div className="bg-white rounded-xl shadow p-4 mb-4">
          <div className="flex items-center gap-2 mb-3">
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') loadGroups(); }}
              className="flex-1 border rounded px-3 py-2 text-sm"
              placeholder="紹介者のメール/IDで検索"
            />
            <button onClick={loadGroups} className="px-4 py-2 rounded bg-coral text-white text-sm">検索</button>
          </div>
          {groupLoading ? (
            <p className="text-sm text-gray-500 py-4">読み込み中…</p>
          ) : groups.length === 0 ? (
            <p className="text-sm text-gray-400 py-4">紹介データがありません</p>
          ) : (
            <div className="space-y-3">
              {groups.map((g) => (
                <div key={g.referrer_id} className="border border-gray-100 rounded-xl p-3">
                  {/* 紹介者 */}
                  <div className="flex items-center flex-wrap gap-2">
                    <span className="text-sm font-bold text-gray-800">
                      {g.referrer_name || `User #${g.referrer_id}`}
                    </span>
                    {g.referrer_member_type && (
                      <span className={classNames('text-xs px-1.5 py-0.5 rounded', MEMBER_TYPE_STYLE[g.referrer_member_type])}>
                        {MEMBER_TYPE_LABEL[g.referrer_member_type]}
                      </span>
                    )}
                    {g.company_name && <span className="text-xs text-gray-400">{g.company_name}</span>}
                    <span className="text-xs text-gray-400 ml-auto">
                      被紹介 {g.referred_count}名（家族{g.family_count}・友人{g.friend_count}）
                    </span>
                  </div>
                  {g.referrer_email && <p className="text-xs text-gray-400">{g.referrer_email}</p>}
                  {/* 被紹介者ツリー */}
                  <div className="mt-2 pl-4 border-l-2 border-gray-100 space-y-1">
                    {g.referred.map((c, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm">
                        <span className="text-gray-300">└</span>
                        <span className="text-gray-700">{c.name || c.email || `User #${c.user_id ?? '—'}`}</span>
                        {c.member_type && (
                          <span className={classNames('text-xs px-1.5 py-0.5 rounded', MEMBER_TYPE_STYLE[c.member_type])}>
                            {MEMBER_TYPE_LABEL[c.member_type]}
                          </span>
                        )}
                        <span className="text-xs"><StatusPill s={c.status} /></span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* 検索/フィルタ（一覧モード） */}
      {viewMode === 'list' && (<>
      <div className="bg-white rounded-xl shadow p-4 mb-4">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          <div className="md:col-span-2">
            <label className="block text-sm text-gray-600 mb-1">{t('admin.referrals.search.query') || '検索（メール/ID部分一致）'}</label>
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="w-full border rounded px-3 py-2"
              placeholder={t('admin.referrals.search.placeholder') || 'example@example.com / 123'}
            />
          </div>

          <div>
            <label className="block text-sm text-gray-600 mb-1">{t('admin.referrals.search.status') || 'ステータス'}</label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value as any)}
              className="w-full border rounded px-3 py-2"
            >
              <option value="">{t('admin.referrals.search.all') || '(すべて)'}</option>
              <option value="pending">{t('admin.referrals.status.pending') || 'pending'}</option>
              <option value="completed">{t('admin.referrals.status.completed') || 'completed'}</option>
              <option value="canceled">{t('admin.referrals.status.canceled') || 'canceled'}</option>
            </select>
          </div>

          {/* 並び替え（2列グリッドで重なり回避 / 小画面では縦並び） */}
          <div>
            <label className="block text-sm text-gray-600 mb-1">{t('admin.referrals.search.orderBy') || '並び替え'}</label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <select
                value={orderBy}
                onChange={(e) => setOrderBy(e.target.value as any)}
                className="border rounded px-3 py-2"
              >
                <option value="created_at">{orderFieldLabel('created_at')}</option>
                <option value="completed_at">{orderFieldLabel('completed_at')}</option>
                <option value="id">{orderFieldLabel('id')}</option>
                <option value="status">{orderFieldLabel('status')}</option>
              </select>
              <select
                value={orderDir}
                onChange={(e) => setOrderDir(e.target.value as any)}
                className="border rounded px-3 py-2"
              >
                <option value="desc">{t('sort.desc') || '逆日期顺序'}</option>
                <option value="asc">{t('sort.asc') || '日期顺序'}</option>
              </select>
            </div>
          </div>

          <div className="flex items-end gap-2">
            <button
              onClick={() => loadRows(true)}
              className="px-4 py-2 rounded bg-coral text-white hover:opacity-90"
            >
              {t('common.search') || '検索'}
            </button>
            <button
              onClick={() => { setQ(''); setStatus(''); }}
              className="px-4 py-2 rounded border hover:bg-gray-50"
            >
              {t('common.clear') || 'クリア'}
            </button>
          </div>
        </div>
      </div>

      {/* 一覧表 */}
      <div className="bg-white rounded-xl shadow overflow-auto">
        {/* ▼ モバイル: カード表示（横スクロールバーを出さずに縦積み） */}
        <div className="md:hidden divide-y">
          {loading ? (
            <div className="px-4 py-6 text-center text-gray-500">{t('common.loading') || '読み込み中…'}</div>
          ) : err ? (
            <div className="px-4 py-6 text-center text-red-600 break-words">{err}</div>
          ) : rows.length === 0 ? (
            <div className="px-4 py-6 text-center text-gray-500">{t('common.no_data') || 'データがありません'}</div>
          ) : (
            rows.map(r => (
              <div key={r.id} className="p-4">
                {/* ヘッダー: ID + ステータス */}
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 text-sm font-semibold text-gray-800 break-all">
                    {t('admin.referrals.table.id') || 'ID'}: {r.id}
                  </div>
                  <span className="shrink-0"><StatusPill s={r.status} /></span>
                </div>

                {/* 各列を「ラベル：値」で縦積み */}
                <div className="mt-3 grid grid-cols-1 gap-y-1.5 text-xs">
                  <div className="min-w-0">
                    <span className="text-gray-400">{t('admin.referrals.table.referrer_id') || '紹介者ID'}：</span>
                    <span className="text-gray-700 break-all">{r.referrer_id}</span>
                  </div>
                  <div className="min-w-0">
                    <span className="text-gray-400">{t('admin.referrals.table.referred_email') || '被紹介メール'}：</span>
                    <span className="text-gray-700 break-all">{r.referred_email || '—'}</span>
                  </div>
                  <div className="min-w-0">
                    <span className="text-gray-400">{t('admin.referrals.table.referred_user_id') || '被紹介ユーザーID'}：</span>
                    <span className="text-gray-700 break-all">{r.referred_user_id ?? '—'}</span>
                  </div>
                  <div className="min-w-0">
                    <span className="text-gray-400">{t('admin.referrals.table.created_at') || '作成'}：</span>
                    <span className="text-gray-700 break-words">{fmt(r.created_at)}</span>
                  </div>
                  <div className="min-w-0">
                    <span className="text-gray-400">{t('admin.referrals.table.completed_at') || '完了'}：</span>
                    <span className="text-gray-700 break-words">{fmt(r.completed_at ?? null)}</span>
                  </div>
                  <div className="min-w-0">
                    <span className="text-gray-400">{t('admin.referrals.table.paid') || '支払済'}：</span>
                    {r.referred_has_paid ? <span className="text-green-700">✅</span> : <span className="text-gray-400">—</span>}
                  </div>
                  <div className="min-w-0">
                    <span className="text-gray-400">{t('admin.referrals.table.commission') || '報酬'}：</span>
                    <span className="text-gray-700 break-all">{r.commission_total ? `¥${r.commission_total.toLocaleString()}` : '—'}</span>
                  </div>
                  <div className="min-w-0">
                    <span className="text-gray-400">{t('admin.referrals.table.first_paid_at') || '初回支払'}：</span>
                    <span className="text-gray-700 break-words">{fmt(r.first_paid_at ?? null)}</span>
                  </div>
                </div>

                {/* 操作 */}
                <div className="mt-3 flex items-center gap-2 flex-wrap">
                  <button
                    onClick={() => openComplete(r)}
                    className="shrink-0 text-xs px-3 py-1.5 rounded border hover:bg-gray-50"
                  >
                    {t('admin.referrals.actions.manual_complete') || '手動完了'}
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        {/* ▼ デスクトップ: テーブル表示 */}
        <table className="hidden md:table min-w-full text-sm">
          <thead>
            <tr className="bg-gray-50 text-gray-600">
              <th className="px-3 py-2 text-left">{t('admin.referrals.table.id') || 'ID'}</th>
              <th className="px-3 py-2 text-left">{t('admin.referrals.table.referrer_id') || '紹介者ID'}</th>
              <th className="px-3 py-2 text-left">{t('admin.referrals.table.referred_email') || '被紹介メール'}</th>
              <th className="px-3 py-2 text-left">{t('admin.referrals.table.referred_user_id') || '被紹介ユーザーID'}</th>
              <th className="px-3 py-2 text-left">{t('admin.referrals.table.status') || 'ステータス'}</th>
              <th className="px-3 py-2 text-left">{t('admin.referrals.table.created_at') || '作成'}</th>
              <th className="px-3 py-2 text-left">{t('admin.referrals.table.completed_at') || '完了'}</th>
              <th className="px-3 py-2 text-left">{t('admin.referrals.table.paid') || '支払済'}</th>
              <th className="px-3 py-2 text-left">{t('admin.referrals.table.commission') || '報酬'}</th>
              <th className="px-3 py-2 text-left">{t('admin.referrals.table.first_paid_at') || '初回支払'}</th>
              <th className="px-3 py-2 text-left">{t('admin.referrals.table.actions') || '操作'}</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={11} className="px-3 py-6 text-center text-gray-500">{t('common.loading') || '読み込み中…'}</td></tr>
            ) : err ? (
              <tr><td colSpan={11} className="px-3 py-6 text-center text-red-600">{err}</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={11} className="px-3 py-6 text-center text-gray-500">{t('common.no_data') || 'データがありません'}</td></tr>
            ) : (
              rows.map(r => (
                <tr key={r.id} className="border-t">
                  <td className="px-3 py-2">{r.id}</td>
                  <td className="px-3 py-2">{r.referrer_id}</td>
                  <td className="px-3 py-2">{r.referred_email || '—'}</td>
                  <td className="px-3 py-2">{r.referred_user_id ?? '—'}</td>
                  <td className="px-3 py-2"><StatusPill s={r.status} /></td>
                  <td className="px-3 py-2">{fmt(r.created_at)}</td>
                  <td className="px-3 py-2">{fmt(r.completed_at ?? null)}</td>
                  <td className="px-3 py-2">
                    {r.referred_has_paid ? <span className="text-green-700">✅</span> : <span className="text-gray-400">—</span>}
                  </td>
                  <td className="px-3 py-2">
                    {r.commission_total ? `¥${r.commission_total.toLocaleString()}` : '—'}
                  </td>
                  <td className="px-3 py-2">{fmt(r.first_paid_at ?? null)}</td>
                  <td className="px-3 py-2">
                    <button
                      onClick={() => openComplete(r)}
                      className="px-3 py-1.5 rounded border hover:bg-gray-50"
                    >
                      {t('admin.referrals.actions.manual_complete') || '手動完了'}
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        <div className="flex items-center justify-between p-3 border-t text-sm">
          <div>
            {t('admin.referrals.paging.showing', { count: rows.length, size: limit }) || `表示: ${rows.length}件 / 1ページ ${limit}件`}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={prevPage}
              disabled={offset === 0}
              className={classNames(
                'px-3 py-1.5 rounded border',
                offset === 0 ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-50'
              )}
            >
              {t('common.prev') || '前へ'}
            </button>
            <button
              onClick={nextPage}
              className="px-3 py-1.5 rounded border hover:bg-gray-50"
            >
              {t('common.next') || '次へ'}
            </button>
            <select
              value={limit}
              onChange={(e) => setLimit(Number(e.target.value))}
              className="ml-2 border rounded px-2 py-1"
            >
              {[20, 50, 100, 200].map(n => <option key={n} value={n}>{n}/page</option>)}
            </select>
          </div>
        </div>
      </div>
      </>)}

      {/* 手動完了ダイアログ */}
      {dlgOpen && dlgTarget && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify中心">
          <div className="w-full max-w-lg bg-white rounded-2xl shadow p-6">
            <h3 className="text-lg font-semibold mb-3">{t('admin.referrals.dialog.title') || '紹介を手動完了'}</h3>
            <div className="space-y-3 text-sm">
              <div>
                <div className="text-gray-600">{t('admin.referrals.dialog.referral_id') || 'Referral ID'}</div>
                <div className="font-mono">{dlgTarget.id}</div>
              </div>
              <div>
                <label className="block text-gray-600 mb-1">{t('admin.referrals.dialog.referred_user_id') || '被紹介ユーザーID'}</label>
                <input
                  type="number"
                  value={dlgReferredUserId}
                  onChange={(e) => setDlgReferredUserId(e.target.value ? Number(e.target.value) : '')}
                  className="w-full border rounded px-3 py-2"
                  placeholder={t('admin.referrals.dialog.referred_user_id_ph') || '例: 123'}
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-gray-600 mb-1">{t('admin.referrals.dialog.referrer_coupon_id') || '紹介者側クーポンID'}</label>
                  <input
                    type="number"
                    value={dlgReferrerCouponId}
                    onChange={(e) => setDlgReferrerCouponId(e.target.value ? Number(e.target.value) : '')}
                    className="w-full border rounded px-3 py-2"
                    placeholder={t('admin.referrals.dialog.referrer_coupon_id_ph') || '例: 10'}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {t('admin.referrals.dialog.default') || '既定'}: {cfg?.referrer_coupon_id || 0}
                  </p>
                </div>
                <div>
                  <label className="block text-gray-600 mb-1">{t('admin.referrals.dialog.referred_coupon_id') || '被紹介者側クーポンID'}</label>
                  <input
                    type="number"
                    value={dlgReferredCouponId}
                    onChange={(e) => setDlgReferredCouponId(e.target.value ? Number(e.target.value) : '')}
                    className="w-full border rounded px-3 py-2"
                    placeholder={t('admin.referrals.dialog.referred_coupon_id_ph') || '例: 11'}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {t('admin.referrals.dialog.default') || '既定'}: {cfg?.referred_coupon_id || 0}
                  </p>
                </div>
              </div>

              {dlgMsg && <p className="text-sm text-red-600">{dlgMsg}</p>}
            </div>

            <div className="flex justify-end gap-2 mt-5">
              <button
                onClick={() => setDlgOpen(false)}
                className="px-4 py-2 rounded border hover:bg-gray-50"
              >
                {t('common.cancel') || 'キャンセル'}
              </button>
              <button
                onClick={submitComplete}
                disabled={dlgBusy}
                className={classNames(
                  'px-4 py-2 rounded text-white',
                  dlgBusy ? 'bg-coral/80 cursor-not-allowed' : 'bg-coral hover:opacity-90'
                )}
              >
                {dlgBusy ? (t('admin.referrals.dialog.running') || '実行中…') : (t('admin.referrals.dialog.submit') || '完了する')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminReferralsPage;
